/**
 * <lazy-video> — defers downloading/playing an autoplaying background video
 * until it scrolls into view, and pauses it again when it leaves.
 *
 * Markup (see snippets/lazy-video.liquid):
 *   <lazy-video data-autoplay>
 *     <video preload="none" muted loop playsinline poster="...">
 *       <source data-src="...mp4" type="video/mp4">
 *     </video>
 *   </lazy-video>
 *
 * The <source> uses data-src (not src) so the browser fetches nothing on load.
 */
if (!customElements.get('lazy-video')) {
  customElements.define(
    'lazy-video',
    class LazyVideo extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('video');
        if (!this.video) return;
        this.loaded = false;
        this.autoplay = this.hasAttribute('data-autoplay');

        // No IntersectionObserver support (very old browsers): load immediately.
        if (!('IntersectionObserver' in window)) {
          this.load();
          this.tryPlay();
          return;
        }

        this.observer = new IntersectionObserver(this.onIntersect.bind(this), {
          rootMargin: '200px 0px',
        });
        this.observer.observe(this);
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }

      load() {
        if (this.loaded) return;
        this.loaded = true;
        this.querySelectorAll('source[data-src]').forEach((source) => {
          source.src = source.dataset.src;
          source.removeAttribute('data-src');
        });
        this.video.load();
      }

      tryPlay() {
        if (!this.autoplay) return;
        this.video.muted = true;
        const playPromise = this.video.play();
        if (playPromise && playPromise.catch) playPromise.catch(() => {});
      }

      onIntersect(entries) {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.load();
            this.tryPlay();
          } else if (this.loaded && this.autoplay) {
            this.video.pause();
          }
        });
      }
    }
  );
}
